import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import { ToolbarSize } from '@atlaskit/editor-common/types';
import { akEditorMobileMaxWidth } from '@atlaskit/editor-shared-styles';
import { componentWithCondition } from '@atlaskit/platform-feature-flags-react';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { WidthObserver } from '@atlaskit/width-detector';
import { isFullPage } from '../../utils/is-full-page';
import { useElementWidth } from './hooks';
import { Toolbar } from './Toolbar';
import { toolbarSizeToWidth, widthToToolbarSize } from './toolbar-size';
// Remove when platform_editor_core_static_emotion_non_central is cleaned up
var toolbar = css(_defineProperty({
  width: '100%',
  position: 'relative'
}, "@media (max-width: ".concat(akEditorMobileMaxWidth, "px)"), {
  gridColumn: '1 / 2',
  gridRow: 2,
  width: 'calc(100% - 30px)',
  margin: "0 ".concat("var(--ds-space-200, 16px)")
}));

// Rename to toolbar when platform_editor_core_static_emotion_non_central is cleaned up
var staticToolbar = css({
  width: '100%',
  position: 'relative'
  // The media query below has been commented out as akEditorMobileMaxWidth is 0px  and thus the styles are never applied.
  // [`@media (max-width: ${akEditorMobileMaxWidth}px)`]: {
  //   gridColumn: '1 / 2',
  //   gridRow: 2,
  //   width: 'calc(100% - 30px)',
  //   margin: `0 ${token('space.200', '16px')}`,
  // },
});
var DynamicStyleToolbarWithSizeDetector = function DynamicStyleToolbarWithSizeDetector(props) {
  var ref = React.useRef(null);
  var _React$useState = React.useState(undefined),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    width = _React$useState2[0],
    setWidth = _React$useState2[1];
  var elementWidth = useElementWidth(ref, {
    skip: typeof width !== 'undefined'
  });
  var defaultToolbarSize = isSSR() && isFullPage(props.appearance) ? ToolbarSize.XXL : undefined;
  var toolbarSize = typeof width === 'undefined' && typeof elementWidth === 'undefined' ? defaultToolbarSize :
  // Ignored via go/ees005
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  widthToToolbarSize(width || elementWidth, props.appearance);
  var toolbarStyle = useMemo(function () {
    var toolbarWidth = isFullPage(props.appearance) && props.twoLineEditorToolbar ? ToolbarSize.S : ToolbarSize.M;
    var toolbarMinWidth = toolbarSizeToWidth(toolbarWidth, props.appearance);
    var isPreviewPanelResponsivenessEnabled = expValEquals('platform_editor_preview_panel_responsiveness', 'isEnabled', true);
    var minWidth = "min-width: ".concat(props.hasMinWidth ? "".concat(toolbarMinWidth, "px") : isPreviewPanelResponsivenessEnabled ? 'fit-content' : '254px');
    return [toolbar, minWidth];
  }, [props.appearance, props.hasMinWidth, props.twoLineEditorToolbar]);
  return (
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    jsx("div", {
      css: toolbarStyle
    }, jsx(WidthObserver, {
      setWidth: setWidth
    }), props.editorView && toolbarSize ?
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
    jsx(Toolbar, _extends({}, props, {
      toolbarSize: toolbarSize
    })) : jsx("div", {
      ref: ref
    }))
  );
};
var StaticStyleToolbarWithSizeDetector = function StaticStyleToolbarWithSizeDetector(props) {
  var ref = React.useRef(null);
  var _React$useState3 = React.useState(undefined),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    width = _React$useState4[0],
    setWidth = _React$useState4[1];
  var elementWidth = useElementWidth(ref, {
    skip: typeof width !== 'undefined'
  });
  var defaultToolbarSize = isSSR() && isFullPage(props.appearance) ? ToolbarSize.XXL : undefined;
  var toolbarSize = typeof width === 'undefined' && typeof elementWidth === 'undefined' ? defaultToolbarSize :
  // Ignored via go/ees005
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  widthToToolbarSize(width || elementWidth, props.appearance);
  var minWidthValue = useMemo(function () {
    if (props.hasMinWidth) {
      var toolbarWidth = isFullPage(props.appearance) && props.twoLineEditorToolbar ? ToolbarSize.S : ToolbarSize.M;
      return "".concat(toolbarSizeToWidth(toolbarWidth, props.appearance), "px");
    } else {
      var isPreviewPanelResponsivenessEnabled = expValEquals('platform_editor_preview_panel_responsiveness', 'isEnabled', true);
      return isPreviewPanelResponsivenessEnabled ? 'fit-content' : '254px';
    }
  }, [props.appearance, props.hasMinWidth, props.twoLineEditorToolbar]);
  return jsx("div", {
    css: staticToolbar,
    style: {
      minWidth: minWidthValue
    }
  }, jsx(WidthObserver, {
    setWidth: setWidth
  }), props.editorView && toolbarSize ? jsx(Toolbar, {
    toolbarSize: toolbarSize,
    items: props.items,
    editorView: props.editorView,
    editorActions: props.editorActions,
    eventDispatcher: props.eventDispatcher,
    providerFactory: props.providerFactory,
    appearance: props.appearance,
    popupsMountPoint: props.popupsMountPoint,
    popupsBoundariesElement: props.popupsBoundariesElement,
    popupsScrollableElement: props.popupsScrollableElement,
    disabled: props.disabled,
    dispatchAnalyticsEvent: props.dispatchAnalyticsEvent,
    containerElement: props.containerElement,
    hasMinWidth: props.hasMinWidth,
    twoLineEditorToolbar: props.twoLineEditorToolbar
  }) : jsx("div", {
    ref: ref
  }));
};
export var ToolbarWithSizeDetector = componentWithCondition(function () {
  return expValEquals('platform_editor_core_static_emotion_non_central', 'isEnabled', true);
}, StaticStyleToolbarWithSizeDetector, DynamicStyleToolbarWithSizeDetector);